"use client";

import React from "react";
import { ArrowRight, Mail, MapPin, Phone, ShieldCheck } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
const nearbyAreas = [{
  name: "Farnborough",
  to: "/AreasWeCoverPage"
}, {
  name: "Basingstoke",
  to: "/AreasWeCoverPage"
}];
export default function FinalQuote() {
  const shouldReduceMotion = useReducedMotion();
  const motionProps = shouldReduceMotion ? {
    initial: {
      opacity: 1,
      y: 0
    },
    animate: {
      opacity: 1,
      y: 0
    }
  } : {
    initial: {
      opacity: 0,
      y: 20
    },
    whileInView: {
      opacity: 1,
      y: 0
    },
    viewport: {
      once: true,
      amount: 0.2
    },
    transition: {
      duration: 0.5,
      ease: "easeOut" as const
    }
  };
  return <section id="finalquotecta" data-nav="light" className="relative bg-background py-16 md:py-24 text-foreground overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
        <motion.div {...motionProps}>
          <Card className="border border-border/80 bg-secondary text-secondary-foreground shadow-xl rounded-2xl overflow-hidden py-0">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 p-8 sm:p-10 lg:p-12 items-center">
              
              {/* Left Column: Copy and Actions */}
              <div className="lg:col-span-7 flex flex-col items-start text-left">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/20 border border-primary/40 text-primary-foreground text-xs font-semibold tracking-wide uppercase mb-4">
                  <ShieldCheck className="size-3.5" />
                  <span>NASC & CHAS Accreditations</span>
                </div>

                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold font-default tracking-tight text-secondary-foreground leading-tight">
                  Request a scaffolding quote
                </h2>

                <p className="mt-4 text-base sm:text-lg text-secondary-foreground/80 max-w-xl leading-relaxed">
                  Speak directly with our contracts team to arrange site visits, technical access plans, and clear commercial pricing.
                </p>

                <div className="mt-8 flex flex-wrap items-center gap-4 w-full sm:w-auto">
                  <Button asChild size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-7 h-12 rounded-md shadow-md text-base transition-colors">
                    <Link to="/ContactRequestQuotePage" className="inline-flex items-center gap-2">
                      <span>Request a quote</span>
                      <ArrowRight className="size-4" />
                    </Link>
                  </Button>

                  <a href="tel:01344929900" className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md border border-border/40 bg-secondary-foreground/10 hover:bg-secondary-foreground/20 text-secondary-foreground hover:text-secondary-foreground font-medium text-base transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                    <Phone className="size-4 text-primary-foreground" />
                    <span className="text-secondary-foreground">01344 929900</span>
                  </a>
                </div>
              </div>

              {/* Right Column: Contact Cards */}
              <div className="lg:col-span-5 flex flex-col gap-4 w-full">
                <div className="bg-card text-card-foreground p-6 rounded-xl border border-border shadow-sm">
                  <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider block mb-1">
                    Direct inquiries
                  </span>
                  <div className="flex items-center justify-between gap-4 mt-2">
                    <div className="flex items-center gap-3">
                      <div className="size-10 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
                        <Mail className="size-5" />
                      </div>
                      <div>
                        <div className="text-xs text-muted-foreground font-medium">Email our estimating desk</div>
                        <a href="mailto:quotes@bmfscaffolding.co.uk" className="text-base font-semibold text-foreground hover:text-primary transition-colors underline-offset-4 hover:underline">
                          quotes@bmfscaffolding.co.uk
                        </a>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-card text-card-foreground p-6 rounded-xl border border-border shadow-sm">
                  <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider block mb-1">
                    Phone line
                  </span>
                  <div className="flex items-center justify-between gap-4 mt-2">
                    <div className="flex items-center gap-3">
                      <div className="size-10 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
                        <Phone className="size-5" />
                      </div>
                      <div>
                        <div className="text-xs text-muted-foreground font-medium">Call operations & dispatch</div>
                        <a href="tel:01344929900" className="text-base font-semibold text-foreground hover:text-primary focus-visible:text-primary transition-colors">
                          01344 929900
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

            </div>

            <Separator className="bg-border/30" />

            {/* Bottom Row: Nearby Areas */}
            <CardContent className="bg-secondary/60 px-8 py-5 sm:px-10 lg:px-12 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center gap-2 text-sm text-secondary-foreground/80 font-medium">
                <MapPin className="size-4 text-primary-foreground" />
                <span>Nearby areas:</span>
                <div className="inline-flex items-center gap-2 flex-wrap">
                  {nearbyAreas.map((area, areaIndex) => <span key={area.name} data-index={areaIndex} className="inline-flex items-center">
                      <Link to={area.to} className="text-secondary-foreground font-semibold hover:text-primary-foreground underline underline-offset-4 transition-colors">
                        {area.name}
                      </Link>
                      {areaIndex < nearbyAreas.length - 1 && <span className="ml-2 text-secondary-foreground/40 font-normal">|</span>}
                    </span>)}
                </div>
              </div>

              <div className="text-xs text-secondary-foreground/70">
                Berkshire, Surrey, Hampshire, Buckinghamshire & South East coverage
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </section>;
}